import React, { useState, useEffect } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/CTXRegisterStyles.css';
import { useAppSelector } from '../../../../redux/hooks';
import Spinner from '../../custom/Spinner/Spinner';
import InstanceSelector from './components/InstanceSelector';
import SyslogServerList from './components/SyslogServerList';
import Pagination from './components/Pagination';
import CustomModal from './components/CustomModal';
import Swal from 'sweetalert2';
import { getListSelectorWithoutCluster, getAvailableServers, getSelectedServers, createAssociationWithSyslog } from './request/request';
import { SyslogServer, Instance, LevelMap } from './types';

export const Level: LevelMap = {
    0: "Emergency",
    1: "Alert",
    2: "Critical",
    3: "Error",
    4: "Warning",
    5: "Notice",
    6: "Informational",
    7: "Debug"
};

const SyslogConfig: React.FC = () => {
    const TenantId = useAppSelector(state => state.TenantSlice.id);
    const SubscriptionId = useAppSelector(state => state.SuscriptionSlice.id);
    const Cloud = useAppSelector(state => state.CloudSlice.Type);
    const Region = useAppSelector(state => state.RegionSlice.name);
    const FlagRegion = useAppSelector(state => state.FlagSlice.value);

    const [selectedInstance, setSelectedInstance] = useState<string>('CTX-R01');
    const [availableServers, setAvailableServers] = useState<SyslogServer[]>([]);
    const [selectedServers, setSelectedServers] = useState<SyslogServer[]>([]);
    const [initialAvailableServers, setInitialAvailableServers] = useState<SyslogServer[]>([]);
    const [initialSelectedServers, setInitialSelectedServers] = useState<SyslogServer[]>([]);
    const [filteredAvailableServers, setFilteredAvailableServers] = useState<SyslogServer[]>([]);
    const [filteredSelectedServers, setFilteredSelectedServers] = useState<SyslogServer[]>([]);
    const [searchTermAvailable, setSearchTermAvailable] = useState<string>('');
    const [searchTermSelected, setSearchTermSelected] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [instances, setInstances] = useState<Instance[]>([]);
    const [instanceUUID, setInstanceUUID] = useState<string>('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [selectedAvailableServers, setSelectedAvailableServers] = useState<SyslogServer[]>([]);
    const [selectedSelectedServers, setSelectedSelectedServers] = useState<SyslogServer[]>([]);
    const tenant = useAppSelector(state => state.TenantSlice.id);
    // const [showAddModal, setShowAddModal] = useState(false);
    // const [showRemoveModal, setShowRemoveModal] = useState(false);

    const fetchData = async () => {
        if (loading) return;
        try {
            if (SubscriptionId !== 0) {
                setLoading(true);
                const response = await getListSelectorWithoutCluster(SubscriptionId, TenantId, Cloud, Region);
                setInstances(response.data);

                if (response.data.length > 0) {
                    setInstanceUUID(response.data[0].id);
                    fetchServers(response.data[0].subscriptionId, response.data[0].id);

                } else {
                    // Clear server data when there are no instances
                    setInitialAvailableServers([]);
                    setInitialSelectedServers([]);
                    setAvailableServers([]);
                    setSelectedServers([]);
                    setFilteredAvailableServers([]);
                    setFilteredSelectedServers([]);
                }
            }
        } catch (error) {
            console.error("error from api show modal", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchServers = async (subscriptionId: number, uuid: string) => {
        try {
            setLoading(true);
            const availableResponse = await getAvailableServers(subscriptionId, uuid, TenantId);
            const selectedResponse = await getSelectedServers(subscriptionId, uuid, TenantId);

            if (availableResponse.data.length > 0 || selectedResponse.data.length > 0) {
                setInitialAvailableServers(availableResponse.data);
                setInitialSelectedServers(selectedResponse.data);
                setAvailableServers(availableResponse.data);
                setSelectedServers(selectedResponse.data);
                setFilteredAvailableServers(availableResponse.data);
                setFilteredSelectedServers(selectedResponse.data);
            } else {
                // Clear server data when there are no servers
                setInitialAvailableServers([]);
                setInitialSelectedServers([]);
                setAvailableServers([]);
                setSelectedServers([]);
                setFilteredAvailableServers([]);
                setFilteredSelectedServers([]);
            }
        } catch (error) {
            console.error("Error fetching servers", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [SubscriptionId, TenantId, Cloud, Region]);

    const hasChanges = () => (
        JSON.stringify(availableServers) !== JSON.stringify(initialAvailableServers) ||
        JSON.stringify(selectedServers) !== JSON.stringify(initialSelectedServers)
    );

    const resetServers = () => {
        setAvailableServers(initialAvailableServers);
        setSelectedServers(initialSelectedServers);
        setFilteredAvailableServers(initialAvailableServers);
        setFilteredSelectedServers(initialSelectedServers);
    };

    const handleInstanceChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newInstanceName = e.target.value;
        setSelectedInstance(newInstanceName);
        const instance = instances.find(inst => inst.name === newInstanceName);
        if (instance) {
            setInstanceUUID(instance.id);
            await fetchServers(instance.subscriptionId, instance.id);
        }
    };

    const moveServer = (server: SyslogServer[], toSelected: boolean) => {
        if (toSelected) {
            const newAvailableServers = availableServers.filter(s => !server.some(srv => srv.id === s.id));
            const newSelectedServers = [...selectedServers, ...server];
            setAvailableServers(newAvailableServers);
            setSelectedServers(newSelectedServers);
            setFilteredAvailableServers(newAvailableServers);
            setFilteredSelectedServers(newSelectedServers);
        } else {
            const newSelectedServers = selectedServers.filter(s => !server.some(srv => srv.id === s.id));
            const newAvailableServers = [...availableServers, ...server];
            setSelectedServers(newSelectedServers);
            setAvailableServers(newAvailableServers);
            setFilteredSelectedServers(newSelectedServers);
            setFilteredAvailableServers(newAvailableServers);
        }
    };


    const handleLevelChange = (id: number, level: string) => {
        const updateServerLevel = (servers: SyslogServer[]) =>
            servers.map(s => s.id === id ? { ...s, level } : s);

        const updatedSelectedServers = updateServerLevel(selectedServers);
        const updatedFilteredSelectedServers = updateServerLevel(filteredSelectedServers);

        setSelectedServers(updatedSelectedServers);
        setFilteredSelectedServers(updatedFilteredSelectedServers);
    };

    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const handleSave = () => {
        setShowSaveModal(true);
    };

    const handleConfirmCancel = () => {
        resetServers();
        setAvailablePage(1);
        setSelectedPage(1);
        setShowCancelModal(false);
    };

    const handleConfirmSave = async () => {
        const dataToSend = selectedServers.map(server => ({
            id: server.id,
            level: parseInt(server.level || '0')
        }));

        try {
            await createAssociationWithSyslog(instanceUUID, dataToSend, tenant);
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Configuration saved!',
                showConfirmButton: true,
                timer: 1500
            });
        } catch (error) {
            console.error("Error creating association:", error);
        }

        setInitialAvailableServers(availableServers);
        setInitialSelectedServers(selectedServers);
        setFilteredAvailableServers(availableServers);
        setFilteredSelectedServers(selectedServers);
        setShowSaveModal(false);
    };

    const handleSearchChangeAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTermAvailable(e.target.value);

        if (e.target.value === '') {
            setFilteredAvailableServers(availableServers);
            setAvailablePage(1);
            return;
        }

        const filteredServers = availableServers.filter(server =>
            (server.ip && server.ip.toLowerCase().includes(e.target.value.toLowerCase())) ||
            (server.description && server.description.toLowerCase().includes(e.target.value.toLowerCase()))
        );

        setFilteredAvailableServers(filteredServers);
        setAvailablePage(1);
    };

    const handleSearchChangeSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTermSelected(e.target.value);

        if (e.target.value === '') {
            setFilteredSelectedServers(selectedServers);
            setSelectedPage(1);
            return;
        }

        const filteredServers = selectedServers.filter(server =>
            (server.ip && server.ip.toLowerCase().includes(e.target.value.toLowerCase())) ||
            (server.description && server.description.toLowerCase().includes(e.target.value.toLowerCase())) ||
            (server.level && Level[parseInt(server.level)]?.toLowerCase().includes(e.target.value.toLowerCase()))
        );

        setFilteredSelectedServers(filteredServers);
        setSelectedPage(1);
    };

    const handleSelectAvailableServer = (server: SyslogServer) => {
        setSelectedAvailableServers(prev =>
            prev.includes(server)
                ? prev.filter(s => s !== server)
                : [...prev, server]
        );
    };

    const handleSelectSelectedServer = (server: SyslogServer) => {
        setSelectedSelectedServers(prev =>
            prev.includes(server)
                ? prev.filter(s => s !== server)
                : [...prev, server]
        );
    };

    // const handleAdd = () => {
    //     setShowAddModal(true);
    // };

    // const handleRemove = () => {
    //     setShowRemoveModal(true);
    // };

    const handleConfirmAdd = () => {
        moveServer(selectedAvailableServers, true);
        setSelectedAvailableServers([]);
    };

    const handleConfirmRemove = () => {
        moveServer(selectedSelectedServers, false);
        setSelectedSelectedServers([]);
    };

    // Pagination for available servers
    const [availablePage, setAvailablePage] = useState(1);
    const [availableRecordsPerPage, setAvailableRecordsPerPage] = useState(5);

    const indexOfLastAvailable = availablePage * availableRecordsPerPage;
    const indexOfFirstAvailable = indexOfLastAvailable - availableRecordsPerPage;
    const currentAvailableServers = filteredAvailableServers.slice(indexOfFirstAvailable, indexOfLastAvailable);

    const paginateAvailable = (pageNumber: number) => setAvailablePage(pageNumber);

    // Pagination for selected servers
    const [selectedPage, setSelectedPage] = useState(1);
    const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(5);

    const indexOfLastSelected = selectedPage * selectedRecordsPerPage;
    const indexOfFirstSelected = indexOfLastSelected - selectedRecordsPerPage;
    const currentSelectedServers = filteredSelectedServers.slice(indexOfFirstSelected, indexOfLastSelected);

    // Función para refrescar la página
    const reloadDevice = () => {
        window.location.reload();
    };

    return (
        <>
            {loading && <Spinner />}
            <div className="card">
                <div className="card-body py-3">
                    <div className="tab-content">
                        <div className="syslog-config">
                            <div className="refresh-button" style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                <button className="btn-light-marino f-parrafo" onClick={reloadDevice}>
                                    <i className="text-gray-100 bi bi-arrow-clockwise"></i>
                                </button>
                            </div>
                            <InstanceSelector
                                instances={instances}
                                selectedInstance={selectedInstance}
                                handleInstanceChange={handleInstanceChange}
                            />
                            <div className="syslog-columns">
                                <SyslogServerList
                                    title="Available"
                                    servers={currentAvailableServers}
                                    searchTerm={searchTermAvailable}
                                    handleSearchChange={handleSearchChangeAvailable}
                                    handleMoveServer={moveServer}
                                    handleLevelChange={handleLevelChange}
                                    handleSelectServer={handleSelectAvailableServer}
                                    pagination={
                                        <Pagination
                                            page={availablePage}
                                            recordsPerPage={availableRecordsPerPage}
                                            setPage={paginateAvailable}
                                            setRecordsPerPage={setAvailableRecordsPerPage}
                                            totalRecords={filteredAvailableServers.length}
                                        />
                                    }
                                    isSelected={false}
                                />
                                <div className="syslog-column-buttons">
                                    <button
                                        className="btn btn-primary add-button"
                                        onClick={handleConfirmAdd}
                                        disabled={selectedAvailableServers.length === 0}
                                    >
                                        <i className="fas fa-arrow-right custom-arrow "></i>
                                    </button>
                                    <button
                                        className="btn btn-danger remove-button"
                                        onClick={handleConfirmRemove}
                                        disabled={selectedSelectedServers.length === 0}
                                    >
                                        <i className="fas fa-arrow-left custom-arrow "></i>
                                    </button>
                                </div>
                                <SyslogServerList
                                    title="Selected"
                                    servers={currentSelectedServers}
                                    searchTerm={searchTermSelected}
                                    handleSearchChange={handleSearchChangeSelected}
                                    handleMoveServer={moveServer}
                                    handleLevelChange={handleLevelChange}
                                    handleSelectServer={handleSelectSelectedServer}
                                    pagination={
                                        <Pagination
                                            page={selectedPage}
                                            recordsPerPage={selectedRecordsPerPage}
                                            setPage={setSelectedPage}
                                            setRecordsPerPage={setSelectedRecordsPerPage}
                                            totalRecords={filteredSelectedServers.length}
                                        />
                                    }
                                    isSelected={true}
                                />
                            </div>
                            <div className="syslog-footer">
                                <button className="btn-cancel btn border border-pink text-pink f-parrafo custom-btn" onClick={handleCancel} disabled={!hasChanges()}>Undo</button>
                                <button className="blueDark-button" onClick={handleSave} disabled={!hasChanges()}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CustomModal
                show={showCancelModal}
                handleClose={() => setShowCancelModal(false)}
                handleConfirm={handleConfirmCancel}
                title="Unsaved Changes"
            >
                Are you sure you want to discard the changes?
            </CustomModal>
            <CustomModal
                show={showSaveModal}
                handleClose={() => setShowSaveModal(false)}
                handleConfirm={handleConfirmSave}
                title="Save Changes"
            >
                Are you sure you want to save the changes?
            </CustomModal>
            {/* <CustomModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                handleConfirm={handleConfirmAdd}
                title="Add Servers"
            >
                Are you sure you want to add the selected servers?
            </CustomModal>
            <CustomModal
                show={showRemoveModal}
                handleClose={() => setShowRemoveModal(false)}
                handleConfirm={handleConfirmRemove}
                title="Remove Servers"
            >
                Are you sure you want to remove the selected servers?
            </CustomModal> */}
        </>
    );
};

export default SyslogConfig;